import { SpeakerOperation, SpeakerStatusResponse, SpeakerTelemetry } from '/@/types/speaker'

export interface NormalizedSpeakerState extends Omit<SpeakerTelemetry, 'psdk_index'> {
  detected: boolean
  psdk_index: string
}

export interface SpeakerPayloadIdentity {
  index?: number
  model?: string
  control_source?: unknown
  payload_sn?: string
  payload_index?: string
  payload_name?: string
  psdk_index?: number | string
}

const SPEAKER_NAME_PATTERN = /(^|\b)(speaker|as1)(\b|$)|喊话/i
const CHILD_KEYS = [
  'psdk_widget_values',
  'psdkWidgetValues',
  'psdk_state_info',
  'psdkStateInfo',
  'speakers',
  'speaker_status',
  'speakerStatus',
  'data',
  'output',
]

function definedValue<T> (...values: Array<T | null | undefined>): T | undefined {
  for (const value of values) {
    if (value !== undefined && value !== null) {
      return value
    }
  }
  return undefined
}

function toOptionalNumber (value: unknown): number | undefined {
  if (value === '' || value === null || value === undefined) {
    return undefined
  }
  const number = Number(value)
  return Number.isFinite(number) ? number : undefined
}

const PSDK_SENTINEL = 65535

/**
 * Every PSDK payload carries a speaker block; non-speakers (the M4TD
 * searchlight, for example) fill it with 65535 sentinels.
 */
function isSentinelSpeakerBlock (speaker: Record<string, any> | null | undefined): boolean {
  if (!speaker || typeof speaker !== 'object') {
    return false
  }
  return Number(speaker.system_state) === PSDK_SENTINEL && Number(speaker.work_mode) === PSDK_SENTINEL
}

function directSpeakerState (raw: unknown): NormalizedSpeakerState | null {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) {
    return null
  }
  const value = raw as Record<string, any>
  if (isSentinelSpeakerBlock(value.speaker)) {
    return null
  }
  const embedded = value.speaker && typeof value.speaker === 'object' ? value.speaker : {}
  const name = String(definedValue(value.psdk_name, value.psdkName, embedded.psdk_name, embedded.psdkName) || '')
  const rawPsdkIndex = definedValue(value.psdk_index, value.psdkIndex, embedded.psdk_index, embedded.psdkIndex)
  const psdkIndex = rawPsdkIndex === undefined ? '' : String(rawPsdkIndex)
  const rawPayloadIndex = definedValue(value.payload_index, value.payloadIndex, embedded.payload_index, embedded.payloadIndex)
  const payloadIndex = rawPayloadIndex === undefined ? '' : String(rawPayloadIndex)
  const detected = value.detected === true || !!value.speaker || SPEAKER_NAME_PATTERN.test(name)
  if (!detected || (psdkIndex === '' && !name)) {
    return null
  }
  return {
    detected: true,
    psdk_index: psdkIndex,
    payload_index: payloadIndex || undefined,
    psdk_name: name || 'Speaker',
    psdk_sn: String(definedValue(value.psdk_sn, embedded.psdk_sn) || '') || undefined,
    psdk_version: String(definedValue(value.psdk_version, embedded.psdk_version) || '') || undefined,
    psdk_lib_version: String(definedValue(value.psdk_lib_version, embedded.psdk_lib_version) || '') || undefined,
    work_mode: toOptionalNumber(definedValue(value.work_mode, embedded.work_mode)),
    play_mode: toOptionalNumber(definedValue(value.play_mode, embedded.play_mode)),
    play_volume: toOptionalNumber(definedValue(value.play_volume, embedded.play_volume)),
    system_state: toOptionalNumber(definedValue(value.system_state, embedded.system_state)),
    play_file_name: String(definedValue(value.play_file_name, embedded.play_file_name) || '') || undefined,
    play_file_md5: String(definedValue(value.play_file_md5, embedded.play_file_md5) || '') || undefined,
    source: String(definedValue(value.source, embedded.source) || '') || undefined,
  }
}

function mergeSpeakerState (
  primary: NormalizedSpeakerState,
  fallback?: NormalizedSpeakerState | null
): NormalizedSpeakerState {
  if (!fallback) {
    return primary
  }
  const merged = { ...fallback }
  Object.entries(primary).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      ;(merged as any)[key] = value
    }
  })
  return merged
}

function collectSpeakerStates (source: unknown, results: NormalizedSpeakerState[], seen: Set<object>, depth = 0) {
  if (!source || depth > 5) {
    return
  }
  if (Array.isArray(source)) {
    source.forEach(value => collectSpeakerStates(value, results, seen, depth + 1))
    return
  }
  if (typeof source !== 'object' || seen.has(source as object)) {
    return
  }
  seen.add(source as object)
  const direct = directSpeakerState(source)
  if (direct) {
    results.push(direct)
  }
  const value = source as Record<string, unknown>
  CHILD_KEYS.forEach(key => collectSpeakerStates(value[key], results, seen, depth + 1))
}

export function findSpeakerState (...sources: unknown[]): NormalizedSpeakerState | null {
  const candidates: NormalizedSpeakerState[] = []
  collectSpeakerStates(sources, candidates, new Set<object>())
  if (candidates.length === 0) {
    return null
  }
  return candidates.reduceRight<NormalizedSpeakerState>((merged, candidate) => mergeSpeakerState(candidate, merged), candidates[candidates.length - 1])
}

export function speakerStateFromStatus (status?: SpeakerStatusResponse | null) {
  return findSpeakerState((status?.speakers || []).map(speaker => ({
    ...speaker,
    detected: true,
  })))
}

export function isCompletePayloadIndex (value: unknown): boolean {
  return /^\d+-\d+-\d+$/.test(String(value ?? '').trim())
}

/**
 * Resolve the AS1 identity used by DJI payload-authority APIs. The numeric
 * psdk_index is only a speaker command address and must never be promoted to a
 * payload_index here.
 */
export function resolveSpeakerAuthorityPayload (
  speaker: NormalizedSpeakerState | null | undefined,
  payloads: SpeakerPayloadIdentity[]
): SpeakerPayloadIdentity | null {
  if (!speaker) {
    return null
  }
  const candidates = payloads.filter(payload => isCompletePayloadIndex(payload.payload_index))
  const explicitPayloadIndex = String(speaker.payload_index ?? '').trim()
  const speakerSn = String(speaker.psdk_sn ?? '').trim()
  const speakerPsdkIndex = String(speaker.psdk_index)

  if (isCompletePayloadIndex(explicitPayloadIndex)) {
    const matched = candidates.find(payload => payload.payload_index === explicitPayloadIndex) ||
      candidates.find(payload => speakerSn && payload.payload_sn === speakerSn)
    return {
      ...(matched || {}),
      model: matched?.model || 'AS1',
      payload_name: matched?.payload_name || speaker.psdk_name || 'Speaker',
      payload_sn: matched?.payload_sn || speaker.psdk_sn,
      payload_index: explicitPayloadIndex,
      psdk_index: speaker.psdk_index,
    }
  }

  const matchedBySn = speakerSn
    ? candidates.find(payload => String(payload.payload_sn ?? '').trim() === speakerSn)
    : undefined
  if (matchedBySn) {
    return { ...matchedBySn }
  }

  const matchedByPsdkIndex = candidates.find(payload =>
    payload.psdk_index !== undefined &&
    payload.psdk_index !== null &&
    String(payload.psdk_index) === speakerPsdkIndex
  )
  if (matchedByPsdkIndex) {
    return { ...matchedByPsdkIndex }
  }

  const namedCandidates = candidates.filter(payload =>
    SPEAKER_NAME_PATTERN.test(`${payload.payload_name || ''} ${payload.model || ''}`)
  )
  return namedCandidates.length === 1 ? { ...namedCandidates[0] } : null
}

export function normalizeSpeakerOperation (source: unknown): SpeakerOperation | null {
  if (!source || typeof source !== 'object') {
    return null
  }
  const value = source as Record<string, any>
  const data = value.data && typeof value.data === 'object' ? value.data : value
  const operationId = String(data.operation_id || data.operationId || '')
  const rawPsdkIndex = definedValue(data.psdk_index, data.psdkIndex)
  const psdkIndex = rawPsdkIndex === undefined ? '' : String(rawPsdkIndex)
  const status = String(data.status || '')
  const md5 = String(data.md5 || data.play_file_md5 || '')
  if (!operationId && psdkIndex === '' && !status && !md5) {
    return null
  }
  const percent = toOptionalNumber(data.percent ?? data.progress)
  return {
    operation_id: operationId || undefined,
    dock_sn: String(data.dock_sn || data.dockSn || '') || undefined,
    psdk_index: psdkIndex === '' ? undefined : psdkIndex,
    md5: md5 || undefined,
    status: status || undefined,
    percent: percent === undefined ? undefined : Math.max(0, Math.min(100, percent)),
    step_key: String(data.step_key || data.stepKey || '') || undefined,
    result: data.result,
  }
}
